import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';
import { getAuthDeferred } from '@/utils/firebase';
import { GreetLogo } from '@/components/brand/GreetLogo';
import GoogleIcon from '@/components/Auth/GoogleIcon';
import { PRESSABLE_CLASS } from '@/components/Auth/AuthPageShell';
import { cn } from '@/lib/utils';

const REDIRECT_URI_KEY = 'greetai_extension_auth_redirect_uri';

const searchSchema = z.object({
  // Optional so Firebase redirect returns (which may drop query params) still render.
  redirect_uri: z.string().url().optional(),
});

export const Route = createFileRoute('/extension-auth')({
  ssr: false,
  validateSearch: zodValidator(searchSchema),
  component: ExtensionAuthPage,
});

function isAllowedRedirect(uri: string): boolean {
  try {
    const parsed = new URL(uri);
    if (parsed.protocol === 'https:' && parsed.hostname.endsWith('.chromiumapp.org')) {
      return true;
    }
    // Firefox has no chromiumapp.org; its extension returns to our own page.
    if (parsed.origin === window.location.origin && parsed.pathname === '/extension-signed-in') {
      return true;
    }
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function rememberRedirectUri(uri: string) {
  try {
    sessionStorage.setItem(REDIRECT_URI_KEY, uri);
  } catch {
    // ignore quota / private mode
  }
}

function readStoredRedirectUri(): string | null {
  try {
    return sessionStorage.getItem(REDIRECT_URI_KEY);
  } catch {
    return null;
  }
}

async function finishAuth(
  user: {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    getIdToken: (force?: boolean) => Promise<string>;
    refreshToken: string;
  },
  redirectUri: string,
) {
  const idToken = await user.getIdToken(true);
  const params = new URLSearchParams({
    id_token: idToken,
    refresh_token: user.refreshToken || '',
    uid: user.uid,
    email: user.email || '',
    name: user.displayName || '',
    picture: user.photoURL || '',
  });
  try {
    sessionStorage.removeItem(REDIRECT_URI_KEY);
  } catch {
    // ignore
  }
  window.location.replace(`${redirectUri}#${params.toString()}`);
}

function ExtensionAuthPage() {
  const search = Route.useSearch();
  const auth = useMemo(() => getAuthDeferred(), []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const redirectUri = useMemo(() => {
    const fromSearch = search.redirect_uri;
    if (fromSearch && isAllowedRedirect(fromSearch)) {
      rememberRedirectUri(fromSearch);
      return fromSearch;
    }
    const stored = readStoredRedirectUri();
    if (stored && isAllowedRedirect(stored)) {
      return stored;
    }
    return null;
  }, [search.redirect_uri]);

  const allowed = Boolean(redirectUri);

  useEffect(() => {
    if (!redirectUri) {
      setError(
        'Open sign-in from the extension to continue.',
      );
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const redirectResult = await getRedirectResult(auth);
        if (cancelled) return;
        if (redirectResult?.user) {
          setBusy(true);
          await finishAuth(redirectResult.user, redirectUri);
          return;
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Redirect sign-in failed');
          setBusy(false);
        }
      }
    })();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user || cancelled) return;
      try {
        setBusy(true);
        await finishAuth(user, redirectUri);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to mint ID token');
        setBusy(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [auth, redirectUri]);

  const signIn = async () => {
    if (!redirectUri) return;
    setBusy(true);
    setError(null);
    rememberRedirectUri(redirectUri);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      // Prefer popup (works in a normal tab). Fall back to redirect if blocked.
      try {
        const result = await signInWithPopup(auth, provider);
        await finishAuth(result.user, redirectUri);
      } catch (popupErr: unknown) {
        const code =
          popupErr && typeof popupErr === 'object' && 'code' in popupErr
            ? String((popupErr as { code: string }).code)
            : '';
        // User closed the popup — don't bounce into redirect.
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          setBusy(false);
          return;
        }
        // Nested popups / COOP often block popup inside identity windows.
        if (
          code === 'auth/popup-blocked' ||
          code === 'auth/operation-not-supported-in-this-environment'
        ) {
          await signInWithRedirect(auth, provider);
          return;
        }
        throw popupErr;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
      setBusy(false);
    }
  };

  // Always dark, matching the extension's side panel the viewer just came from.
  return (
    <main className="dark flex min-h-dvh items-center justify-center bg-[#090909] px-4 text-white antialiased">
      <div className="w-full max-w-[360px]">
        <GreetLogo className="h-8 w-auto" />
        <h1 className="mt-10 text-balance text-[28px] font-semibold leading-tight tracking-[-0.01em]">
          Learn to speak while you watch
        </h1>
        <p className="mt-2 text-pretty text-[15px] text-white/60">
          Sign in to save your words and practice across devices.
        </p>
        <button
          type="button"
          onClick={signIn}
          disabled={!allowed || busy}
          className={cn(
            'mt-8 flex h-12 w-full items-center justify-center gap-2.5 rounded-xl bg-white text-[15px] font-semibold text-[#111] shadow-[inset_0_1px_0_rgba(255,255,255,0.26)] hover:bg-white/90 disabled:cursor-default disabled:opacity-60 disabled:active:scale-100',
            PRESSABLE_CLASS,
          )}>
          <GoogleIcon className="h-[18px] w-[18px]" aria-hidden="true" />
          {busy ? 'Signing in…' : 'Sign in with Google'}
        </button>
        {error ? (
          <p
            role="alert"
            className="mt-4 rounded-xl border border-red-500/25 bg-red-500/5 px-3 py-2 text-[13px] text-[#ff6961]">
            {error}
          </p>
        ) : (
          <p className="mt-4 text-[13px] text-white/45">
            You’ll go straight back to the extension.
          </p>
        )}
      </div>
    </main>
  );
}
